import type { UserManager } from "oidc-client-ts";
import { currentConfig, loadConfig } from "./config";

/**
 * Who the console signs in as, and which tenant it works in.
 *
 * Three kinds of sign-in exist: Microsoft (OpenID Connect, when configured), ZeaOps' own
 * session after an email and password sign-in, and in development a configured person.
 * Nobody signs up: every account starts from an invite.
 */

let manager: Promise<UserManager> | null = null;

function local(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

function sessionStore(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

/** Microsoft sign-in is offered when the deployment has an OIDC client set up. */
export function microsoftConfigured(): boolean {
  const cfg = currentConfig();
  return cfg.authMode === "oidc" && Boolean(cfg.oidcAuthority && cfg.oidcClientId);
}

/** The OIDC client, loaded only when Microsoft sign-in is on so other builds stay small. */
function oidc(): Promise<UserManager> {
  manager ??= (async () => {
    const cfg = currentConfig();
    if (!cfg.oidcAuthority || !cfg.oidcClientId) {
      throw new Error(
        "Microsoft sign-in is on but oidcAuthority or oidcClientId is missing in config.json",
      );
    }
    const { UserManager: Manager, WebStorageStateStore } = await import("oidc-client-ts");
    const store = sessionStore();
    return new Manager({
      authority: cfg.oidcAuthority,
      client_id: cfg.oidcClientId,
      redirect_uri: `${window.location.origin}/auth/callback`,
      post_logout_redirect_uri: `${window.location.origin}/sign-in`,
      response_type: "code",
      scope: cfg.oidcScope ?? "openid profile email",
      automaticSilentRenew: true,
      ...(store ? { userStore: new WebStorageStateStore({ store }) } : {}),
    });
  })();
  return manager;
}

/* ZeaOps' own session, after an email and password sign-in ---------------------------- */

const SESSION_KEY = "zeaops.session";

interface StoredSession {
  token: string;
  expiresAt: string;
}

export function sessionToken(): string | null {
  const raw = local()?.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as StoredSession;
    if (!s.token || Date.parse(s.expiresAt) <= Date.now() + 30_000) {
      local()?.removeItem(SESSION_KEY);
      return null;
    }
    return s.token;
  } catch {
    local()?.removeItem(SESSION_KEY);
    return null;
  }
}

export function storeSession(token: string, expiresAt: string): void {
  try {
    local()?.setItem(SESSION_KEY, JSON.stringify({ token, expiresAt } satisfies StoredSession));
  } catch {
    /* storage unavailable: the session lasts until the page reloads */
  }
}

export function clearSession(): void {
  try {
    local()?.removeItem(SESSION_KEY);
  } catch {
    /* nothing stored */
  }
}

/* The tenant the console works in --------------------------------------------------------- */

const TENANT_KEY = "zeaops.tenant";

export function currentTenant(): string | null {
  try {
    return local()?.getItem(TENANT_KEY) ?? null;
  } catch {
    return null;
  }
}

export function setCurrentTenant(slug: string | null): void {
  try {
    if (slug) local()?.setItem(TENANT_KEY, slug);
    else local()?.removeItem(TENANT_KEY);
  } catch {
    /* storage unavailable: the API picks the first tenant */
  }
}

/* Signing in and out ----------------------------------------------------------------------- */

const PUBLIC = [
  /^\/sign-in/,
  /^\/forgot-password/,
  /^\/reset-password\//,
  /^\/invite\//,
  /^\/no-access/,
  /^\/auth\/callback/,
];

/** Screens anyone can open without signing in. */
export function isPublicPath(path: string): boolean {
  return PUBLIC.some((p) => p.test(path));
}

/** The bearer token for API calls, or null when nobody is signed in. */
export async function getAccessToken(): Promise<string | null> {
  const cfg = await loadConfig();
  const own = sessionToken();
  if (own) return own;
  // Development: the person chosen from the account menu, or else whoever the API's
  // ZEAOPS_DEV_USER_EMAIL names, so the console and the API never disagree about who that is.
  if (cfg.authMode === "dev") return `dev:${devUser() ?? ""}`;
  if (microsoftConfigured()) {
    const user = await (await oidc()).getUser();
    if (user && !user.expired) return user.access_token;
  }
  return null;
}

export function goToSignIn(next = window.location.pathname + window.location.search): void {
  const target =
    isPublicPath(next) || next === "/" ? "/sign-in" : `/sign-in?next=${encodeURIComponent(next)}`;
  if (window.location.pathname !== "/sign-in") window.location.assign(target);
}

/** True when someone is signed in; otherwise sends the browser to the sign-in screen. */
export async function ensureSignedIn(): Promise<boolean> {
  const token = await getAccessToken();
  if (token) return true;
  goToSignIn();
  return false;
}

export async function signInWithMicrosoft(next = "/"): Promise<void> {
  const client = await oidc();
  await client.signinRedirect({ state: next });
}

export async function completeSignIn(): Promise<string> {
  const user = await (await oidc()).signinRedirectCallback();
  return typeof user.state === "string" ? user.state : "/";
}

export async function signOut(): Promise<void> {
  const cfg = await loadConfig();
  clearSession();
  setCurrentTenant(null);
  if (cfg.authMode === "dev") {
    setDevUser(null);
    window.location.assign("/sign-in");
    return;
  }
  if (microsoftConfigured()) {
    const client = await oidc();
    const user = await client.getUser();
    if (user) {
      await client.signoutRedirect();
      return;
    }
  }
  window.location.assign("/sign-in");
}

/* Development: act as another seeded person to try roles and approval scopes -------------- */

const DEV_USER_KEY = "zeaops.devUser";

export function devUser(): string | null {
  try {
    return window.localStorage.getItem(DEV_USER_KEY);
  } catch {
    return null;
  }
}

export function setDevUser(email: string | null): void {
  try {
    if (email) window.localStorage.setItem(DEV_USER_KEY, email);
    else window.localStorage.removeItem(DEV_USER_KEY);
  } catch {
    /* storage unavailable: the default dev user stays in effect */
  }
}
